/**
 * The one answer to "where is the backend?".
 *
 * On the web the answer is allowed to be "" - a relative /v1/... works because
 * Cloudflare rewrites it to the Render backend, and local dev has the Vite
 * proxy doing the same job. Four files each derived this independently, and
 * all four were subtly the same trap: inside the packaged Android app there is
 * no Cloudflare and no Vite. The WebView's origin is a synthetic localhost, so
 * a relative URL sends the login POST to a server that does not exist, and the
 * failure surfaces as "wrong password" to somebody whose password was right.
 *
 * So: the env var wins when configured (it is, on Pages), and the native shell
 * falls back to the production API by name, because inside an installed app
 * there is no such thing as a relative backend. The plain-web fallback stays
 * "" deliberately - local dev and previews must keep hitting their proxy, not
 * production.
 *
 * Module-load timing is safe here: Capacitor's bridge is injected into the
 * page before any bundle script runs, so the shell is detectable by the time
 * this file is evaluated.
 */

/** The backend's public address, for when the page cannot use a relative URL. */
export const PROD_API_ORIGIN = "https://resumark-api.onrender.com";

function inNativeShell(): boolean {
  if (typeof window === "undefined") return false;
  const w = window as unknown as { Capacitor?: { isNativePlatform?: () => boolean } };
  return typeof w.Capacitor?.isNativePlatform === "function" && w.Capacitor.isNativePlatform();
}

export function resolveApiBase(): string {
  const configured =
    import.meta.env.VITE_API_URL || import.meta.env.VITE_API_BASE || "";
  if (configured) return String(configured).replace(/\/+$/, "");
  return inNativeShell() ? PROD_API_ORIGIN : "";
}
